import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { LogService, SentryHttpErrorHandlerService } from '@core/services';
import { AccountService } from '@features/server-side-authentication';
import { catchError, retry, switchMap, throwError, timer } from 'rxjs';
import { HttpErrorStrategyFactory } from '../strategies';

const RETRY_CONFIG = {
  count: 3,
  delay: 1000,
} as const;

export const httpErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const sentryHandler = inject(SentryHttpErrorHandlerService);
  const strategyFactory = inject(HttpErrorStrategyFactory);
  const accountService = inject(AccountService);
  const log = inject(LogService);

  const errorStrategies = strategyFactory.createStrategies();
  let retryAttempt = 0;

  const addToken = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    if (accountService.accessToken) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${accountService.accessToken}`,
        },
      });
    }
    return request;
  };

  const requestWithToken = addToken(req);

  return next(requestWithToken).pipe(
    /**
     * Returns an observable that emits after `retryDelay` milliseconds if the error was a network error (status === 0).
     * Otherwise, rethrows the error. This is used to implement retry logic with a delay after network errors.
     * @param error the error that was thrown
     */
    retry({
      count: RETRY_CONFIG.count,
      delay: (error: HttpErrorResponse) => {
        retryAttempt++;

        if (error.status === 0) {
          sentryHandler.addRetryBreadcrumb(requestWithToken, retryAttempt, RETRY_CONFIG.count);
          return timer(RETRY_CONFIG.delay);
        }

        return throwError(() => error);
      },
    }),

    catchError((error: HttpErrorResponse) => {
      sentryHandler.captureHttpError(error, requestWithToken, {
        retryAttempt: retryAttempt > 0 ? retryAttempt : undefined,
        maxRetries: RETRY_CONFIG.count,
        timestamp: new Date().toISOString(),
      });

      sentryHandler.addErrorBreadcrumb(error, requestWithToken);

      const strategy = errorStrategies.find((s) => s.canHandle(error));

      if (strategy) {
        const result = strategy.handle(error, requestWithToken);

        if (result && typeof result === 'object' && 'pipe' in result) {
          return result.pipe(
            switchMap((response: any) => {
              if (response?.shouldRetry) {
                const retryRequest = addToken(req);
                return next(retryRequest);
              }
              return throwError(() => error);
            }),
            catchError((retryError) => {
              return throwError(() => retryError);
            }),
          );
        }
      } else {
        sentryHandler.captureUnhandledError(error, requestWithToken);
      }

      return throwError(() => error);
    }),
  );
};
